import { Observable, of, tap, throwError } from 'rxjs';
import { catchError, finalize, shareReplay } from 'rxjs/operators';

const CACHE_EXPIRATION_TIME = 86400000; // 24 hours

/**
 * This class caches the previous data from the last 24 hours and returns the cached data if it exists.
 * If the data is not cached, it will fetch the data from the dataFactory and cache it.
 * If the data is not cached and the refresh flag is true, it will fetch the data from the dataFactory and cache it.
 *
 * The cache is not cleaned up but rather overwritten with the new data.
 * @example
 * const cache = new WorkTimeCache<YearMonth, OvertimeDetail<SumOfCategoriesWithTemporarySum>[]>({
 *   dataFactory: (month) => {
 *     return this.userWorktimeGatewayService.getPayrollCategoriesSummary<
 *       OvertimeDetail<SumOfCategoriesWithTemporarySum>
 *     >(userId, month, true);
 *   },
 * });
 */

export class WorkTimeCache<K, T> {
  private readonly cache = new Map<
    K,
    {
      value: T;
      timestamp: number;
    }
  >();

  private readonly inFlight = new Map<K, Observable<T>>();

  constructor(
    private args: {
      dataFactory: (key: K) => Observable<T>;
    },
  ) {}

  set(key: K, value: T): void {
    this.cache.set(key, { value, timestamp: Date.now() });
  }

  get(
    key: K,
    refresh = false,
    options?: { onFallbackToCache?: () => void },
  ): Observable<T> {
    const cachedValue = this.cache.get(key);
    if (
      cachedValue &&
      cachedValue.timestamp + CACHE_EXPIRATION_TIME > Date.now() &&
      !refresh
    ) {
      return of(cachedValue.value);
    } else {
      const existing = this.inFlight.get(key);
      if (existing) {
        return existing;
      } else {
        const request$ = this.args.dataFactory(key).pipe(
          tap((value) => {
            this.set(key, value);
          }),
          shareReplay(1),
          catchError((err) => {
            if (refresh && cachedValue) {
              options?.onFallbackToCache?.();
              return of(cachedValue.value);
            }
            return throwError(() => err);
          }),
          finalize(() => this.inFlight.delete(key)),
        );
        this.inFlight.set(key, request$);
        return request$;
      }
    }
  }

  remove(key: K): void {
    this.cache.delete(key);
  }
}
